import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, Check, Loader2, Skull, Trophy, Sparkles, Gift, Infinity as InfIcon, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const Founder = () => {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/founder/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const buy = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/founder/checkout", {
        email, name, origin_url: window.location.origin,
      });
      if (data.status === "already_active" && data.member_url) {
        window.location.href = data.member_url;
        return;
      }
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const price = status?.price_label || "$99";
  const remaining = status?.slots_remaining ?? 100;
  const soldOut = status && !status.available;

  return (
    <div className="grain min-h-screen" data-testid="founder-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="mb-10 text-center">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-3">presale · one time · lifetime</div>
          <h1 className="font-display text-5xl sm:text-6xl text-[#f4f0ec] mb-4">Founding Member Pass</h1>
          <p className="text-[#c7bebe] text-lg max-w-xl mx-auto">
            Lifetime access to every case. One payment. No subscription.
          </p>
        </div>

        {/* Scarcity line */}
        {status && !soldOut && (
          <div className="card-noir p-4 mb-6 text-center" data-testid="founder-scarcity">
            <span className="text-[#f4f0ec] font-bold" translate="no">{remaining}</span>
            <span className="text-[#a39797]"> of {status.slots_total} Founder slots left at </span>
            <span className="text-[#d4af37] font-bold" translate="no">{price}</span>
          </div>
        )}

        {/* Benefits */}
        <div className="card-noir p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={16} className="text-[#d4af37]" />
            <div className="font-display text-xl text-[#f4f0ec]">What you get</div>
          </div>
          <ul className="space-y-3 text-sm text-[#c7bebe]">
            <li className="flex gap-3"><InfIcon size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">Every current case</b> — our full 15+ case Vault, unlocked today.</span></li>
            <li className="flex gap-3"><Sparkles size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">Every new case we write</b> — forever. New drops auto-added to your library.</span></li>
            <li className="flex gap-3"><Gift size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">Monthly mini-mysteries</b> delivered to your inbox.</span></li>
            <li className="flex gap-3"><Check size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">25% off everything else</b> — Case Maker, party packs, Crew Night. Permanent discount code.</span></li>
            <li className="flex gap-3"><Trophy size={16} className="text-[#4ade80] shrink-0 mt-0.5" />
              <span><b className="text-[#f4f0ec]">Founder #N forever</b> — your number appears on your member page.</span></li>
          </ul>
        </div>

        {/* Checkout */}
        <form onSubmit={buy} className="card-noir p-6 border-[#d4af37]" data-testid="founder-form">
          <div className="text-xs tracking-widest text-[#a39797] mb-4">BECOME A FOUNDER</div>
          <input type="email" required placeholder="Your email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-3 focus:outline-none focus:border-[#d4af37]"
            data-testid="founder-email" />
          <input placeholder="Your name (shown as 'Founder #N')"
            value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md px-3 py-3 text-[#f4f0ec] text-sm mb-4 focus:outline-none focus:border-[#d4af37]"
            data-testid="founder-name" />
          <button type="submit" disabled={busy || soldOut}
            className="btn-primary w-full justify-center disabled:opacity-50"
            data-testid="founder-buy-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Skull size={14} />}
            {soldOut ? "Sold out" : `Claim my pass — ${price}`}
          </button>
          <div className="text-xs text-[#a39797] mt-3 text-center">
            Secure checkout via Stripe. Your library link is emailed within 24 hours (usually seconds).
          </div>
        </form>

        <p className="text-center text-xs text-[#5a5050] mt-8">
          Lifetime access means as long as WasntMe.Party operates. See{" "}
          <Link to="/terms" className="text-[#d4af37]">terms</Link>.
        </p>
      </div>
    </div>
  );
};

export const FounderThanks = () => {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("polling");
  const [memberUrl, setMemberUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!sid) { setState("missing"); return; }
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/founder/thanks?session_id=${sid}`);
        if (data.paid && data.member_url) {
          setMemberUrl(data.member_url);
          setState("ready");
          setTimeout(() => navigate(`/members/${data.member_token}`), 1500);
          return;
        }
      } catch (err) { /* retry */ }
      tries += 1;
      if (tries < 20) setTimeout(tick, 1500);
      else setState("timeout");
    };
    tick();
  }, [sid, navigate]);

  return (
    <div className="grain min-h-screen flex items-center justify-center">
      <div className="card-noir p-8 max-w-md text-center" data-testid="founder-thanks">
        {state === "polling" && (
          <>
            <Loader2 size={28} className="mx-auto animate-spin text-[#d4af37] mb-3" />
            <div className="font-display text-xl text-[#f4f0ec] mb-2">Minting your pass…</div>
            <div className="text-xs text-[#a39797]">Assigning your Founder number and opening your library.</div>
          </>
        )}
        {state === "ready" && (
          <>
            <Trophy size={32} className="mx-auto text-[#d4af37] mb-3" />
            <div className="font-display text-2xl text-[#f4f0ec] mb-2">Welcome, Founder.</div>
            <a href={memberUrl} className="btn-primary text-sm" data-testid="founder-open-library">
              Open my library <ArrowRight size={14} />
            </a>
          </>
        )}
        {state === "timeout" && (
          <>
            <div className="font-display text-xl text-[#f4f0ec] mb-2">Taking longer than expected</div>
            <div className="text-xs text-[#a39797] mb-3">
              Payment confirmed but we're still provisioning. Check your email in 2 minutes.
            </div>
            <Link to="/" className="btn-ghost text-sm">Back home</Link>
          </>
        )}
        {state === "missing" && (
          <>
            <div className="font-display text-xl text-[#f4f0ec] mb-2">No session found</div>
            <Link to="/founder" className="btn-ghost text-sm">Back to Founder page</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default Founder;
